/**
 * PageShell Component
 *
 * Shared outer wrapper for utility pages. Internally composes the new
 * PageHero primitive so PageShell-using pages share a single look with
 * pages that adopt PageHero + PageTabs + GlassSection directly. The
 * public API is preserved so existing call-sites need no changes.
 *
 * For new traveler pages, prefer composing PageHero + PageTabs +
 * GlassSection directly — that gives more layout control. Use PageShell
 * for non-traveler utility pages where the simpler API is sufficient.
 *
 * Usage (unchanged):
 *   <PageShell title="Tokens & Products" subtitle="Manage your tokens">
 *     <GlassSection>...</GlassSection>
 *   </PageShell>
 */

import React from 'react';
import { PageHero } from '../PageHero';
import './PageShell.css';

export const PAGE_SHELL_WIDTHS = {
  NARROW: 'narrow',   // ~720px  — forms, settings
  DEFAULT: 'default', // ~1100px — most utility pages
  WIDE: 'wide',       // ~1400px — dashboards, grids
  FULL: 'full'        // 100%    — admin tables, maps
};

export function PageShell({
  title,
  subtitle,
  eyebrow,
  actions,
  width = PAGE_SHELL_WIDTHS.DEFAULT,
  className = '',
  headerClassName = '',
  contentClassName = '',
  hero = true,
  children,
  ...rest
}) {
  const wrapperClass = [
    'page-shell',
    `page-shell--${width}`,
    className
  ].filter(Boolean).join(' ');

  const showHero = hero && (title || subtitle || actions || eyebrow);

  return (
    <div className={wrapperClass} {...rest}>
      {showHero && (
        <PageHero
          eyebrow={eyebrow}
          title={title}
          subtitle={subtitle}
          actions={actions}
          className={headerClassName}
        />
      )}
      <div className={`page-shell__content ${contentClassName}`.trim()}>
        {children}
      </div>
    </div>
  );
}

export default PageShell;
